import {
  type AiAssistAction,
  type AiAssistResponse,
  type AiImageResponse,
  type AiStatus,
  ApiError,
  descriptionToHtml,
  queryKeys,
} from '@mevaser/shared'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Sparkles } from 'lucide-react'
import { useState } from 'react'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

/**
 * Pulls the `productImages` row id out of an own-origin
 * `/products/images/{uuid}` URL — the shape every product image URL takes,
 * whether uploaded or AI-generated (see `apps/api/.../catalog/routes.ts` and
 * `apps/api/.../ai/service.ts`). A pasted external URL has no such id, so a
 * draft or edit built from it simply has nothing to look at.
 */
export function imageIdFromUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined
  const match = url.match(/\/products\/images\/([0-9a-f-]{36})(?:$|[?#])/i)
  return match?.[1]
}

function useAiStatus() {
  const { can } = useAuth()
  return useQuery({
    queryKey: queryKeys.ai.status(),
    queryFn: () => api.get<AiStatus>('/ai/status'),
    enabled: can('ai:assist'),
    staleTime: 60_000,
  })
}

const QUICK_ACTIONS: Array<{ action: AiAssistAction; label: string; needsValue: boolean }> = [
  { action: 'draft', label: 'Write a draft', needsValue: false },
  { action: 'improve', label: 'Improve', needsValue: true },
  { action: 'shorten', label: 'Shorten', needsValue: true },
  { action: 'fix-grammar', label: 'Fix grammar', needsValue: true },
]

export interface AiAssistContext {
  fieldLabel: string
  hints?: Record<string, string>
  imageId?: string
  maxLength?: number
  /** Earlier messages in the thread, oldest first — a draft then replies to them (t1232). */
  conversation?: Array<{ from: 'customer' | 'staff'; text: string }>
}

/**
 * A sparkle button that opens a small dialog offering AI writing help for one
 * field — draft/improve/shorten/fix-grammar as one-click actions, or a custom
 * instruction. Renders nothing when AI assist isn't permitted or configured,
 * the same "silently absent" pattern other permission-gated controls use.
 */
export function AiAssistButton({
  target,
  value,
  onApply,
  context,
}: {
  target: 'rich-text' | 'plain-text'
  value: string | null
  onApply: (text: string) => void
  context: AiAssistContext
}) {
  const { can } = useAuth()
  const { data: status } = useAiStatus()
  const [open, setOpen] = useState(false)

  if (!can('ai:assist') || !status?.text) return null

  return (
    <>
      <button
        type="button"
        aria-label={`AI writing help for ${context.fieldLabel}`}
        onClick={() => setOpen(true)}
        className="flex size-6 items-center justify-center rounded text-foreground-muted transition-colors hover:bg-surface-sunken hover:text-primary"
      >
        <Sparkles className="size-3.5" />
      </button>
      <AiAssistDialog
        open={open}
        onOpenChange={setOpen}
        target={target}
        value={value}
        context={context}
        onApply={(text) => {
          onApply(text)
          setOpen(false)
        }}
      />
    </>
  )
}

function AiAssistDialog({
  open,
  onOpenChange,
  target,
  value,
  context,
  onApply,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  target: 'rich-text' | 'plain-text'
  value: string | null
  context: AiAssistContext
  onApply: (text: string) => void
}) {
  const toast = useToast()
  const [instruction, setInstruction] = useState('')
  const [result, setResult] = useState<string | null>(null)

  const hasValue = !!value?.trim()

  const generate = useMutation({
    mutationFn: (action: AiAssistAction) =>
      api.post<AiAssistResponse>('/ai/assist', {
        target,
        action,
        instruction: action === 'custom' ? instruction.trim() : undefined,
        currentValue: value,
        context: {
          fieldLabel: context.fieldLabel,
          hints: context.hints,
          imageId: context.imageId,
          conversation: context.conversation,
        },
        maxLength: context.maxLength,
      }),
    onSuccess: (res) => setResult(res.text),
    onError: (error) =>
      toast.error('Could not generate', error instanceof ApiError ? error.message : undefined),
  })

  const reset = () => {
    setResult(null)
    setInstruction('')
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset()
        onOpenChange(next)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>AI writing help — {context.fieldLabel}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {result === null ? (
            <>
              <div className="flex flex-wrap gap-2">
                {QUICK_ACTIONS.filter((a) => !a.needsValue || hasValue).map((a) => (
                  <Button
                    key={a.action}
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={generate.isPending}
                    onClick={() => generate.mutate(a.action)}
                  >
                    {a.label}
                  </Button>
                ))}
              </div>
              <div className="space-y-2">
                <Textarea
                  rows={2}
                  placeholder="Or describe what you want changed…"
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={!instruction.trim() || generate.isPending}
                  onClick={() => generate.mutate('custom')}
                >
                  {generate.isPending ? 'Generating…' : 'Generate'}
                </Button>
              </div>
            </>
          ) : (
            <div className="space-y-2">
              <p className="text-xs font-medium text-foreground-muted">Preview</p>
              {target === 'rich-text' ? (
                // Server-sanitized HTML (sanitizeDescription), same as a stored description renders.
                <div
                  className="rich-text rounded-md border border-border bg-surface-sunken px-2.5 py-1.5 text-sm"
                  dangerouslySetInnerHTML={{ __html: descriptionToHtml(result) }}
                />
              ) : (
                <p className="whitespace-pre-wrap rounded-md border border-border bg-surface-sunken px-2.5 py-1.5 text-sm">
                  {result}
                </p>
              )}
              {target === 'plain-text' && context.maxLength ? (
                <p
                  className={`text-xs tabular ${result.length > context.maxLength ? 'text-danger' : 'text-foreground-muted'}`}
                >
                  {result.length} / {context.maxLength} characters
                </p>
              ) : null}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          {result === null ? (
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          ) : (
            <>
              <Button type="button" variant="ghost" onClick={reset}>
                Try again
              </Button>
              <Button type="button" variant="primary" onClick={() => onApply(result)}>
                Use this
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Same idea for images: generates from a text prompt, or edits the current
 * photo when one is already picked. Renders nothing when AI assist isn't
 * permitted or image generation isn't configured.
 */
export function AiImageButton({
  sourceImageId,
  promptHints,
  onApply,
}: {
  sourceImageId?: string
  promptHints?: Record<string, string>
  onApply: (url: string) => void
}) {
  const { can } = useAuth()
  const { data: status } = useAiStatus()
  const [open, setOpen] = useState(false)

  if (!can('ai:assist') || !status?.image) return null

  return (
    <>
      <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(true)}>
        <Sparkles /> Generate with AI
      </Button>
      <AiImageDialog
        open={open}
        onOpenChange={setOpen}
        sourceImageId={sourceImageId}
        promptHints={promptHints}
        onApply={(url) => {
          onApply(url)
          setOpen(false)
        }}
      />
    </>
  )
}

function AiImageDialog({
  open,
  onOpenChange,
  sourceImageId,
  promptHints,
  onApply,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  sourceImageId?: string
  promptHints?: Record<string, string>
  onApply: (url: string) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const defaultPrompt = promptHints
    ? `A clean product photo of ${Object.values(promptHints).filter(Boolean).join(' ')} on a plain white background.`
    : ''
  const [prompt, setPrompt] = useState(defaultPrompt)
  const [result, setResult] = useState<AiImageResponse | null>(null)

  const generate = useMutation({
    mutationFn: () =>
      api.post<AiImageResponse>('/ai/image', {
        prompt: prompt.trim(),
        sourceImageId,
      }),
    onSuccess: (generated) => {
      setResult(generated)
      void queryClient.invalidateQueries({ queryKey: queryKeys.imageLibrary.all() })
    },
    onError: (error) =>
      toast.error(
        'Could not generate the image',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const reset = () => setResult(null)

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset()
        onOpenChange(next)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate a product image</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Textarea
            rows={3}
            placeholder="Describe the image you want…"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={generate.isPending}
          />
          {sourceImageId ? (
            <p className="text-xs text-foreground-muted">
              Starts from the current photo and edits it, rather than generating from nothing. This
              can take up to a minute.
            </p>
          ) : (
            <p className="text-xs text-foreground-muted">
              Generates a new image from the description above. This can take up to a minute.
            </p>
          )}
          {result ? (
            <img
              src={result.url}
              alt=""
              className="h-40 w-40 rounded-md border border-border bg-surface-sunken object-cover"
            />
          ) : null}
        </DialogBody>
        <DialogFooter>
          {result === null ? (
            <>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                loading={generate.isPending}
                disabled={!prompt.trim()}
                onClick={() => generate.mutate()}
              >
                Generate
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="ghost" onClick={reset}>
                Try again
              </Button>
              <Button type="button" variant="primary" onClick={() => onApply(result.url)}>
                Use this image
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
